/**
 * SiteLockControls — header control cluster for the site edit-lock (#82).
 *
 * Renders, in the Site header's right cell (`<SiteLockControls site={site} />`):
 *   - UNLOCKED          → "Lock site" button (holder claim).
 *   - LOCKED, viewer is holder
 *                       → "Unlock" button + EditAccessInbox (incoming requests
 *                          to approve/deny).
 *   - LOCKED, viewer is NOT holder
 *                       → "Request edit access" button (+ a one-line message),
 *                          plus a "Force unlock" button when the viewer is a
 *                          platform-admin (isAdmin claim) or company-admin
 *                          (companyRole === 'admin').
 *
 * Locking is whole-site read-only scoped. Approve TRANSFERS the lock to the
 * requester (one holder at a time). Default `locked` is false (legacy
 * field-less sites read as unlocked).
 *
 * Zustand discipline (project hard rule): subscribe to scalar roots only;
 * never .filter/.map/spread/object-literal inside a selector. All derivation
 * happens in useMemo / local handlers.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Lock, Unlock, KeyRound, ShieldAlert, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { SiteEntity } from '../../types/site';
import { useStore } from '../../store';
import { auth as faAuth } from '../../firebase';
import { requireAdminClaim } from '../../lib/requireAdminClaim';
import { EditAccessInbox } from './EditAccessInbox';

export interface SiteLockControlsProps {
  site: SiteEntity;
}

/**
 * Returns true when the current auth user may write to `site` despite the lock:
 * holder, platform-admin, or company-admin. Mirrors `assertSiteWritable`
 * (store) so the UI affordances and the write guard stay in lock-step.
 */
export function canEditLockedSite(
  site: Pick<SiteEntity, 'locked' | 'lockedByUid'>,
  uid: string | null | undefined,
  opts: { isAdmin: boolean; isCompanyAdmin: boolean },
): boolean {
  if (!site.locked) return true;
  if (uid && site.lockedByUid === uid) return true;
  return opts.isAdmin || opts.isCompanyAdmin;
}

export function SiteLockControls({ site }: SiteLockControlsProps): React.ReactElement {
  // ── Store actions (exact contract names from area DATA+STORE+TYPES). ──
  const lockSite = useStore((s) => s.lockSite);
  const unlockSite = useStore((s) => s.unlockSite);
  const requestEditAccess = useStore((s) => s.requestEditAccess);
  const adminForceUnlock = useStore((s) => s.adminForceUnlock);

  // ── Role scalars (subscribe to roots; derive below). ──
  const companyRole = useStore((s) => s.companyRole);
  const isCompanyAdmin = companyRole === 'admin';

  // Platform-admin is an async ID-token claim (no Zustand scalar). Resolve
  // once on mount; default false until it lands.
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  useEffect(() => {
    let alive = true;
    void requireAdminClaim()
      .then((ok) => {
        if (alive) setIsPlatformAdmin(ok);
      })
      .catch(() => {
        /* claim probe failures fall through to non-admin (least privilege). */
      });
    return () => {
      alive = false;
    };
  }, []);

  const uid = faAuth.currentUser?.uid ?? null;

  // ── Derived lock view (useMemo, never inside a selector). ──
  const locked = site.locked === true;
  const isHolder = locked && uid != null && site.lockedByUid === uid;
  const canOverride = isPlatformAdmin || isCompanyAdmin;

  const holderLabel = useMemo(() => {
    if (!locked) return '';
    return site.lockedByName || site.lockedByEmail || 'another user';
  }, [locked, site.lockedByName, site.lockedByEmail]);

  // ── UI state ──
  const [busy, setBusy] = useState<null | 'lock' | 'unlock' | 'request' | 'force'>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');

  const handleLock = async () => {
    setBusy('lock');
    try {
      await lockSite(site.id);
      toast.success('Site locked for editing — others are now read-only.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to lock site');
    } finally {
      setBusy(null);
    }
  };

  const handleUnlock = async () => {
    setBusy('unlock');
    try {
      await unlockSite(site.id);
      toast.success('Site unlocked.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to unlock site');
    } finally {
      setBusy(null);
    }
  };

  const handleRequest = async () => {
    setBusy('request');
    try {
      await requestEditAccess(site.id, requestMessage.trim() || undefined);
      toast.success(`Edit-access request sent to ${holderLabel}.`);
      setRequestOpen(false);
      setRequestMessage('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send request');
    } finally {
      setBusy(null);
    }
  };

  const handleForceUnlock = async () => {
    setBusy('force');
    try {
      await adminForceUnlock(site.id);
      toast.success(`Force-unlocked — ${holderLabel} has been notified.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to force-unlock');
    } finally {
      setBusy(null);
    }
  };

  // ── UNLOCKED: anyone able to write may claim the lock. ──
  if (!locked) {
    return (
      <div className="flex items-center gap-2 shrink-0" data-testid="site-lock-controls">
        <button
          type="button"
          onClick={handleLock}
          disabled={busy !== null}
          data-testid="site-lock-button"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border-subtle bg-card/30 text-[10px] uppercase tracking-widest text-text-secondary hover:bg-state-hover hover:text-text-primary disabled:opacity-50 transition-colors"
          title="Lock this site so only you can edit it"
        >
          {busy === 'lock' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
          Lock site
        </button>
      </div>
    );
  }

  // ── LOCKED + viewer is the holder: unlock + manage requests. ──
  if (isHolder) {
    return (
      <div className="flex items-center gap-2 shrink-0" data-testid="site-lock-controls">
        <EditAccessInbox site={site} />
        <button
          type="button"
          onClick={handleUnlock}
          disabled={busy !== null}
          data-testid="site-unlock-button"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-primary/50 bg-primary/10 text-[10px] uppercase tracking-widest text-primary hover:bg-primary/20 disabled:opacity-50 transition-colors"
          title="Release the lock so others can edit"
        >
          {busy === 'unlock' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
          Unlock
        </button>
      </div>
    );
  }

  // ── LOCKED + viewer is NOT the holder: request access (+ admin override). ──
  return (
    <div className="flex items-center gap-2 shrink-0" data-testid="site-lock-controls">
      {requestOpen ? (
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
            placeholder="Optional message…"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleRequest();
              if (e.key === 'Escape') setRequestOpen(false);
            }}
            className="w-44 px-2 py-1.5 text-[11px] bg-transparent border-b-2 border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary text-foreground"
            data-testid="edit-access-request-message"
          />
          <button
            type="button"
            onClick={handleRequest}
            disabled={busy !== null}
            data-testid="edit-access-request-send"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-primary/50 bg-primary/10 text-[10px] uppercase tracking-widest text-primary hover:bg-primary/20 disabled:opacity-50 transition-colors"
          >
            {busy === 'request' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
            Send
          </button>
          <button
            type="button"
            onClick={() => { setRequestOpen(false); setRequestMessage(''); }}
            className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setRequestOpen(true)}
          disabled={busy !== null}
          data-testid="edit-access-request-button"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border-subtle bg-card/30 text-[10px] uppercase tracking-widest text-text-secondary hover:bg-state-hover hover:text-text-primary disabled:opacity-50 transition-colors"
          title={`Locked by ${holderLabel} — ask for edit access`}
        >
          <KeyRound className="w-3.5 h-3.5" />
          Request edit access
        </button>
      )}

      {canOverride && (
        <button
          type="button"
          onClick={handleForceUnlock}
          disabled={busy !== null}
          data-testid="site-force-unlock-button"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-danger/50 bg-danger/10 text-[10px] uppercase tracking-widest text-danger hover:bg-danger/20 disabled:opacity-50 transition-colors"
          title="Admin override — force-unlock and notify the current holder"
        >
          {busy === 'force' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />}
          Force unlock
        </button>
      )}
    </div>
  );
}
